import { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router'

import B2becTable from '@/components/B2becTable'
import { useAppDispatch, useAppSelector } from '@/store/hook'
import { selectReOrderIds } from '@/store/selectors/cartSelector'
import { selectDateFormat, selectHideStatus } from '@/store/selectors/configSelector'
import { trackLinkName } from '@/store/slices/adobeAnalyticSlice'
import { reorder } from '@/store/slices/cartSlice'
import { generateDummy } from '@/hooks/useDummyItems'
import usePermission from '@/hooks/usePermission'
import { LINK_NAMES } from '@/libs/constants/adobeAnalytics'
import { MODULE_ORDER_RIGHTS } from '@/libs/constants/modulerights'
import { analyzeLinkName } from '@/libs/utils/adobeAnalysis'

import styles from './OrdersHistoryTable.module.scss'
import useRenderOrderColumns from './tableColumns'

const shapeOfProduct = {
  orderDate: '',
  referenceNumber: '',
  status: null,
  customerId: '',
  shipments: [],
  totalPrice: 0,
}

const fakeItems = generateDummy(6, shapeOfProduct)

const emptyItems = []

const OrdersHistoryTable = (props) => {
  const {
    items = emptyItems,
    currentPage = 1,
    isLoading = false,
    hasActions = true,
    ...tableProps
  } = props
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const dateFormat = useAppSelector(selectDateFormat)
  const { hasPermission: hasReorderPermission } = usePermission(
    MODULE_ORDER_RIGHTS.REORDER_HISTORIC_ORDER,
  )

  const reorderIds = useAppSelector(selectReOrderIds)
  const hideStatusEnabled = useAppSelector(selectHideStatus)

  const rowKey = isLoading ? 'materialNumber' : 'salesOrderNumber'
  const showLoading = isLoading || !dateFormat

  const dataSource = useMemo(() => {
    if (showLoading) {
      return fakeItems
    }
    return items
  }, [items, showLoading])

  const handleReorder = useCallback(
    (salesOrderNumber, canViewCart) => {
      if (!hasReorderPermission) {
        return
      }

      dispatch(
        trackLinkName(
          analyzeLinkName(LINK_NAMES.ORDER_HISTORY_OVERVIEW.RE_ORDER),
        ),
      )
      dispatch(reorder({ salesOrderNumber, canViewCart, navigate }))
    },
    [dispatch, hasReorderPermission],
  )

  const handleAnalyzeLinkName = useCallback(
    (orderNumber) => {
      dispatch(
        trackLinkName(
          analyzeLinkName(
            LINK_NAMES.ORDER_HISTORY_OVERVIEW.ORDER_DETAIL,
            orderNumber,
          ),
        ),
      )
    },
    [dispatch],
  )

  const columns = useRenderOrderColumns({
    isLoading,
    hasActions,
    currentPage,
    reorderIds,
    handleReorder,
    handleAnalyzeLinkName,
    hideStatusColumn: hideStatusEnabled,
  })

  return (
    <B2becTable
      key={dateFormat}
      boxShadow={false}
      className={styles.wrapper}
      columns={columns}
      dataSource={dataSource}
      rowKey={rowKey}
      showSorterTooltip={false}
      {...tableProps}
    />
  )
}

export default OrdersHistoryTable
